import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { FilterField } from '@/components/list/filters';
import { CLASS_FILTER_VALUES, classOf, EXPENSES_CLASSIFIERS, NO_CLASS } from '../internal-class';
import {
  EXPENSES_APPROVE,
  EXPENSES_MANAGE,
  EXPENSES_READ_ALL,
  holds,
  type ExpensesPermissionsState,
} from '../use-expenses-permissions';

/** Gestione and the approvers see the class column (#915 C4, U1 board 2.2). */
export function classifies(permissions: ExpensesPermissionsState): boolean {
  return EXPENSES_CLASSIFIERS.some((code) => holds(permissions, code));
}

/**
 * The class filter: Gestione, or an approver who reads others' expenses. An approver who sees only
 * their own would only filter what they may not classify; finance answers nothing for it (review
 * perms H1), so the filter is not offered.
 */
export function filtersByClass(permissions: ExpensesPermissionsState): boolean {
  return (
    holds(permissions, EXPENSES_MANAGE) ||
    (holds(permissions, EXPENSES_APPROVE) && holds(permissions, EXPENSES_READ_ALL))
  );
}

/**
 * «Classificazione»: one value at a time, as every filter of the shared list (decision 101);
 * «Nessuna» finds what is still to classify.
 */
export function useClassFilterField(): FilterField {
  const { t } = useTranslation('expenses-parity');
  return useMemo(
    () => ({
      key: 'class',
      label: t('class.filter'),
      options: CLASS_FILTER_VALUES.map((value) => ({
        value,
        label: value === NO_CLASS ? t('class.none') : value,
      })),
    }),
    [t],
  );
}

export function ClassHeader({ className }: { readonly className?: string }) {
  const { t } = useTranslation('expenses-parity');
  return (
    <th scope="col" className={cn('px-3 py-2.5 font-semibold', className)}>
      <abbr title={t('class.columnLong')} className="no-underline">
        {t('class.column')}
      </abbr>
    </th>
  );
}

/** The row's class, «—» for none, empty where the caller may not see it (their own note). */
export function ClassCell({
  row,
  className,
}: {
  readonly row: object;
  readonly className?: string;
}) {
  const { t } = useTranslation('expenses-parity');
  const value = classOf(row);
  return (
    <td className={cn('px-3 py-3 font-semibold', className)}>
      {value === undefined ? null : value === null ? (
        <span className="text-muted-foreground" title={t('class.none')}>
          —
        </span>
      ) : (
        value
      )}
    </td>
  );
}
